// ── Inline quick-create component ──

import { api } from '../api.js';
import { showToast } from '../ui.js';
import { escapeHtml } from '../utils.js';
import { reserveTaskKey, releaseTaskKey } from '../lib/task-key-reservation.js';

// (C1483) No per-type prefix map anymore — every CODING key (task/story/bug alike)
// reserves this project's own task_prefix; `type` still rides along as its own field
// (see beginReservation below) so story/bug are distinguishable without a key prefix.
// Was `{ story: 'S', bug: 'B' }` (C1481: `task` already omitted so the server default
// won).
const TYPE_LABELS = { task: 'Task', story: 'Story', bug: 'Bug' };

let activeInstanceId = null;
let boundContainers = [];
let outsideHandler = null;

// Reservation state for the single currently-active instance (only one quick-create
// form can be expanded at a time — expand() collapses any other before opening).
let reservedTask = null;   // the placeholder task returned by reserveTaskKey, or null
let reservedProjectId = null;
let committed = false;     // true once handleSubmit has PATCHed the reservation in place
let reserveSeq = 0;        // guards against a stale reservation resolving after switchType/collapse

// ── Render trigger HTML ──

export function renderQuickCreate(id) {
  return `<div class="quick-create-wrap" data-qc-id="${escapeHtml(id)}">` +
    `<button type="button" class="quick-create-trigger" data-qc-id="${escapeHtml(id)}">+ Add item</button>` +
    `</div>`;
}

// ── Form HTML ──

function renderForm(id, type) {
  let html = `<div class="quick-create-form" data-qc-id="${escapeHtml(id)}">`;
  html += `<div class="quick-create-type-btns">`;
  for (const t of ['task', 'story', 'bug']) {
    html += `<button type="button" class="quick-create-type-btn ${t === type ? 'active' : ''}" data-qc-type="${t}" data-qc-id="${escapeHtml(id)}">${TYPE_LABELS[t]}</button>`;
  }
  html += `</div>`;
  html += `<div class="quick-create-key-row">`;
  html += `<input class="quick-create-key-input" data-qc-id="${escapeHtml(id)}" value="Reserving…" readonly maxlength="50">`;
  html += `<input class="quick-create-title-input" data-qc-id="${escapeHtml(id)}" placeholder="Title..." autofocus disabled>`;
  html += `</div>`;
  html += `</div>`;
  return html;
}

// ── Expand / Collapse ──

function getWrap(id) {
  return document.querySelector(`.quick-create-wrap[data-qc-id="${CSS.escape(id)}"]`);
}

// Release the current reservation (if any, and not already finalized) — shared by
// every dismiss path: collapse, switchType, removeQuickCreate. Deliberately does NOT
// trigger a page re-render: the reservation is never pushed into the caller's tasks
// array (see beginReservation) precisely so releasing never needs to un-render
// anything — a re-render here would also destroy whatever DOM switchType() is in the
// middle of building for the newly-selected type.
function releaseCurrentReservation() {
  reserveSeq++; // invalidate any in-flight reserveTaskKey resolution for this instance
  if (!committed && reservedTask && reservedProjectId != null) {
    releaseTaskKey(reservedProjectId, reservedTask.task_key);
  }
  reservedTask = null;
  reservedProjectId = null;
  committed = false;
}

function collapse(id) {
  if (!id) return;
  releaseCurrentReservation();
  const wrap = getWrap(id);
  if (wrap) {
    wrap.innerHTML = `<button type="button" class="quick-create-trigger" data-qc-id="${escapeHtml(id)}">+ Add item</button>`;
  }
  if (activeInstanceId === id) activeInstanceId = null;
}

// Reserve a key for `type` and populate the (already-rendered) form's key/title
// inputs once it resolves. `mySeq` guards against a stale resolution after the
// user has since switched type or dismissed the form. Deliberately does NOT push
// the placeholder into the caller's tasks array — no live "New task" ghost card
// while reserving (same call as board.js/C963: showing one live risks a re-render
// mid-reservation destroying the very form that's booking it). The board only
// reflects the real task once handleSubmit finalizes it.
async function beginReservation(id, opts, type) {
  const mySeq = ++reserveSeq;
  const wrap = getWrap(id);
  const keyInput = wrap && wrap.querySelector('.quick-create-key-input');
  const titleInput = wrap && wrap.querySelector('.quick-create-title-input');
  try {
    const task = await reserveTaskKey(opts.projectId, { type });
    if (mySeq !== reserveSeq) {
      // Superseded by a type switch, collapse, or teardown — release immediately,
      // don't touch DOM that may belong to a different instance/type now.
      releaseTaskKey(opts.projectId, task.task_key);
      return;
    }
    reservedTask = task;
    reservedProjectId = opts.projectId;
    if (keyInput) keyInput.value = task.task_key;
    if (titleInput) titleInput.disabled = false;
  } catch (err) {
    if (mySeq !== reserveSeq) return;
    showToast(err.message || 'Failed to reserve task key', 'error');
    collapse(id);
  }
}

function expand(id, opts) {
  if (activeInstanceId && activeInstanceId !== id) collapse(activeInstanceId);
  const wrap = getWrap(id);
  if (!wrap) return;
  wrap.innerHTML = renderForm(id, 'task');
  activeInstanceId = id;
  const titleInput = wrap.querySelector('.quick-create-title-input');
  if (titleInput) titleInput.focus();
  beginReservation(id, opts, 'task');
}

function switchType(id, newType, opts) {
  const wrap = getWrap(id);
  if (!wrap) return;
  releaseCurrentReservation(); // release the OLD type's reservation before booking the new one
  wrap.innerHTML = renderForm(id, newType);
  activeInstanceId = id;
  const titleInput = wrap.querySelector('.quick-create-title-input');
  if (titleInput) titleInput.focus();
  beginReservation(id, opts, newType);
}

// ── Submit ──

async function handleSubmit(id, opts) {
  const wrap = getWrap(id);
  if (!wrap) return;
  if (!reservedTask) return; // reservation still in flight; inputs are disabled anyway

  const titleInput = wrap.querySelector('.quick-create-title-input');
  const activeTypeBtn = wrap.querySelector('.quick-create-type-btn.active');

  const title = titleInput?.value.trim();
  const type = activeTypeBtn?.dataset.qcType || 'task';

  if (!title) {
    if (titleInput) titleInput.focus();
    return;
  }

  titleInput.disabled = true;

  const triggerEl = wrap;
  const ctx = opts.getContext ? opts.getContext(triggerEl) : {};
  const taskKey = reservedTask.task_key;

  try {
    const payload = {
      title,
      description: '', // C1078: no description input in this UI; explicit '' finalizes the
                        // reservation and clears the placeholder sentinel description
      category: 'CODING',
      type,
      status: ctx.status || 'pending',
      priority: ctx.priority ?? 0,
    };
    if (ctx.parentId) payload.parent_id = ctx.parentId;
    else payload.is_objective = true; // top-level web create = objective (C1341)

    const { task } = await api.patch(`/projects/${opts.projectId}/tasks/${taskKey}`, payload);

    const tasks = opts.getTasks ? opts.getTasks(triggerEl) : opts.allTasks;
    if (tasks && Array.isArray(tasks)) tasks.push(task);

    committed = true;
    collapse(id);
    showToast('Task created', 'success');

    if (opts.onCreated) await opts.onCreated(task);

    // Animate new card
    requestAnimationFrame(() => {
      const newCard = document.querySelector(`[data-task-key="${CSS.escape(task.task_key)}"]`);
      if (newCard) {
        newCard.classList.add('quick-create-appear');
        setTimeout(() => newCard.classList.remove('quick-create-appear'), 300);
      }
    });
  } catch (err) {
    titleInput.disabled = false;
    showToast(err.message || 'Failed to create task', 'error');
  }
}

// ── Bind delegation ──

export function bindQuickCreate(container, opts) {
  function onClick(e) {
    // Trigger click → expand
    const trigger = e.target.closest('.quick-create-trigger');
    if (trigger) {
      e.preventDefault();
      e.stopPropagation();
      const id = trigger.dataset.qcId;
      expand(id, opts);
      return;
    }

    // Type button click → switch type
    const typeBtn = e.target.closest('.quick-create-type-btn');
    if (typeBtn) {
      e.preventDefault();
      e.stopPropagation();
      const id = typeBtn.dataset.qcId;
      const newType = typeBtn.dataset.qcType;
      switchType(id, newType, opts);
      return;
    }
  }

  function onKeydown(e) {
    const titleInput = e.target.closest('.quick-create-title-input');
    const keyInput = e.target.closest('.quick-create-key-input');
    if (!titleInput && !keyInput) return;

    const id = e.target.dataset.qcId;
    if (!id) return;

    if (e.key === 'Enter') {
      e.preventDefault();
      handleSubmit(id, opts);
      return;
    }
    if (e.key === 'Escape') {
      e.preventDefault();
      collapse(id);
      return;
    }
  }

  container.addEventListener('click', onClick);
  container.addEventListener('keydown', onKeydown);
  boundContainers.push({ container, onClick, onKeydown });

  // Outside click to dismiss
  if (!outsideHandler) {
    outsideHandler = (e) => {
      if (!activeInstanceId) return;
      const wrap = getWrap(activeInstanceId);
      if (wrap && !wrap.contains(e.target)) {
        collapse(activeInstanceId);
      }
    };
    document.addEventListener('mousedown', outsideHandler);
  }
}

// ── Cleanup ──

export function removeQuickCreate() {
  if (activeInstanceId) collapse(activeInstanceId);
  for (const { container, onClick, onKeydown } of boundContainers) {
    container.removeEventListener('click', onClick);
    container.removeEventListener('keydown', onKeydown);
  }
  boundContainers = [];
  if (outsideHandler) {
    document.removeEventListener('mousedown', outsideHandler);
    outsideHandler = null;
  }
  activeInstanceId = null;
}
